/**
 * PresenceAvatars — Slack-like "who's here right now" strip.
 *
 * Pure presentation. The caller passes a ``presence`` map keyed by ``sub``
 * (shape defined in ``useWorkbenchChannel``). The current user is hidden
 * from the list because seeing their own avatar next to "You" is noise.
 *
 * Click a chip to see the email tooltip; no other interaction for v1.
 */
import React, { useMemo } from 'react';

function initialsFor(entry) {
  const email = (entry?.email || '').trim();
  if (email) {
    const [name] = email.split('@');
    const parts = name.split(/[.\-_]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  return (entry?.sub || '??').slice(0, 2).toUpperCase();
}

// Stable, accessible, distinguishable palette (Tailwind 500s).
const PALETTE = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
  'bg-violet-500', 'bg-sky-500', 'bg-teal-500', 'bg-fuchsia-500',
];

function colorFor(sub) {
  let hash = 0;
  for (let i = 0; i < (sub || '').length; i += 1) {
    hash = (hash * 31 + sub.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
}

export default function PresenceAvatars({ presence, currentUserSub, maxVisible = 5 }) {
  const others = useMemo(() => {
    const list = Object.values(presence || {}).filter(
      (p) => p && p.sub && p.sub !== currentUserSub,
    );
    // Most recently active first — helps when the room is busy.
    list.sort((a, b) => (b.lastSeenMs || 0) - (a.lastSeenMs || 0));
    return list;
  }, [presence, currentUserSub]);

  if (!others.length) return null;

  const visible = others.slice(0, maxVisible);
  const overflow = others.length - visible.length;

  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-1.5">
        {visible.map((p) => (
          <div
            key={p.sub}
            title={p.email || p.sub}
            className={`relative inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white ${colorFor(p.sub)}`}
          >
            {initialsFor(p)}
            {p.typing ? (
              <span
                aria-hidden="true"
                className="absolute -bottom-0.5 -right-0.5 inline-block h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-white"
              />
            ) : null}
          </div>
        ))}
      </div>
      {overflow > 0 ? (
        <span
          className="inline-flex h-6 min-w-[20px] items-center justify-center rounded-full bg-gray-200 px-1.5 text-[10px] font-semibold text-gray-600 ring-2 ring-white"
          title={`${overflow} more editor${overflow === 1 ? '' : 's'}`}
        >
          +{overflow}
        </span>
      ) : null}
      <span className="ml-1 hidden text-[11px] text-gray-500 sm:inline" aria-live="polite">
        {others.length === 1 ? '1 editor' : `${others.length} editors`}
      </span>
    </div>
  );
}
